import { useAppDispatch, useAppSelector } from "Redux/hooks";
import {
  askToDelete,
  setRichTextFocus,
  setShowPopUp,
} from "feature/producer/actions";
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  ElementType,
  Link,
  LinkId,
  Reference,
  ReferenceId,
  Term,
  TermId,
} from "types/types";
import Button from "../Components/Atoms/Button";
import ElementCards from "../Components/Molecule/ElementCards";
import ElementSelector from "../Components/Molecule/ElementSelector";
import PopUp from "../Components/Molecule/PopUp";
import PopUpAddElements from "../Components/Molecule/PopUpAddElements";
import SearchBar from "../Components/Molecule/SearchBar";
import SidePage from "../Components/Molecule/SidePage";
import {
  addUserElementToBackend,
  changeElement,
  changeUserElementToBackend,
  deleteUserElementToBackend,
  selectUserLinks,
  selectUserRefs,
  selectUserTerms,
  setElementText,
} from "../Redux/UserSlice";
import Layout from "./Layout";
import { selectStyle } from "Redux/appSlice";
import {
  selectDeleteElement,
  selectLinkToAdd,
  selectPopUpType,
  selectRefToAdd,
  selectRichTextFocus,
  selectShowPopUp,
  selectTermToAdd,
} from "Redux/ProducerSlice";

/*
  @function ProducerElement
  render page for edit pages
  @param elements {list} - elements to render
*/
export default function ProducerElement({
  elements = {},
  Editor,
  type,
}: {
  elements?:
    | Record<TermId, Term>
    | Record<LinkId, Link>
    | Record<ReferenceId, Reference>;
  Editor: any;
  type: ElementType;
}) {
  const dispatch = useAppDispatch();
  const { color1 } = useAppSelector(selectStyle);
  const showPopUp = useAppSelector(selectShowPopUp);
  const popUpType = useAppSelector(selectPopUpType);
  const termToAdd = useAppSelector(selectTermToAdd);
  const linkToAdd = useAppSelector(selectLinkToAdd);
  const refToAdd = useAppSelector(selectRefToAdd);
  const richTextFocus = useAppSelector(selectRichTextFocus);
  const deleteElement = useAppSelector(selectDeleteElement);
  const userTerms = useAppSelector(selectUserTerms);
  const userLinks = useAppSelector(selectUserLinks);
  const userRefs = useAppSelector(selectUserRefs);
  const params = useParams();

  return (
    <Layout
      barAddOns={
        <div className="flex flex-row justify-between">
          <SearchBar />
        </div>
      }
    >
      {showPopUp && color1 && (
        <PopUpAddElements
          type={popUpType}
          onSet={(type, val) => {
            const resolveCallback = () => {
              if (type === "Term") {
                // don't show pop up by default, commenting
                // dispatch(setShowPopUp());
                return termToAdd?.callback;
              }
              if (type === "Link") {
                // don't show pop up by default, commenting
                //dispatch(setShowPopUp());
                return linkToAdd?.callback;
              }
              if (type === "Reference") {
                return refToAdd?.callback;
              }
            };
            dispatch(
              addUserElementToBackend({
                orgId: params.orgId || "",
                elementData: val,
                // @ts-ignore
                type: type,
                callback: resolveCallback(),
              })
            );
          }}
          onCancel={() => dispatch(setShowPopUp())}
        />
      )}
      {richTextFocus &&
        Object.keys(richTextFocus).length > 0 &&
        richTextFocus.term !== undefined &&
        userTerms && (
          <SidePage onClose={() => dispatch(setRichTextFocus({}))}>
            <ElementSelector
              content={userTerms[richTextFocus.term].description}
              onUpdate={(a) => {
                if (richTextFocus?.term) {
                  dispatch(
                    setElementText({
                      type: ElementType.Term,
                      id: richTextFocus.term.toString(),
                      value: a,
                    })
                  );
                }
              }}
            />
          </SidePage>
        )}
      {richTextFocus &&
        Object.keys(richTextFocus).length > 0 &&
        userLinks &&
        richTextFocus.link !== undefined && (
          <SidePage onClose={() => dispatch(setRichTextFocus({}))}>
            <ElementSelector
              content={userLinks[richTextFocus.link].description}
              onUpdate={(a) => {
                if (richTextFocus?.link) {
                  dispatch(
                    setElementText({
                      type: ElementType.Link,
                      id: richTextFocus.link.toString(),
                      value: a,
                    })
                  );
                }
              }}
            />
          </SidePage>
        )}
      {richTextFocus &&
        userRefs &&
        Object.keys(richTextFocus).length > 0 &&
        richTextFocus.reference !== undefined && (
          <SidePage onClose={() => dispatch(setRichTextFocus({}))}>
            <ElementSelector
              content={userRefs[richTextFocus.reference].description}
              onUpdate={(a) => {
                if (richTextFocus?.reference) {
                  dispatch(
                    setElementText({
                      type: ElementType.Reference,
                      id: richTextFocus.reference.toString(),
                      value: a,
                    })
                  );
                }
              }}
            />
          </SidePage>
        )}
      {deleteElement !== undefined && (
        <PopUp
          text={"are you sure you want to delete element?"}
          onContinue={async () => {
            if (deleteElement && deleteElement.element === "element") {
              if (deleteElement) {
                dispatch(
                  deleteUserElementToBackend({
                    element: deleteElement.elementToDelete,
                    type: deleteElement.type,

                    orgId: params.orgId || "",
                  })
                );
              }
            }

            dispatch(askToDelete(undefined));
          }}
          onCancel={() => dispatch(askToDelete(undefined))}
        />
      )}
      <div className=" overflow-hidden">
        <div className="mt-24 m-auto w-4/5 ">
          <div className="h-[89vh] p-2 flex flex-col overflow-y-auto overflow-x-hidden">
            {Object.keys(elements).map((v, i) => (
              <Card
                key={i}
                element={elements[v]}
                Editor={Editor}
                pageType={type}
              />
            ))}
            <Button
              label="ADD ELEMENT"
              onClick={() =>
                dispatch(
                  addUserElementToBackend({
                    orgId: params.orgId || "",
                    type: type,
                    callback: () => {},
                    elementData: {
                      name: "insert name here",
                      code: "insert code here",
                      description: { text: [[]] },
                      link: "insert link here",
                      address: "insert address here",
                    },
                  })
                )
              }
            />
          </div>
        </div>
      </div>
    </Layout>
  );
}

/*
sorting 
              .sort((a, b) => {
                if (type === "Reference") {
                  return elements[a].code.localeCompare(elements[b].code);
                }

                return elements[a].name.localeCompare(elements[b].name);
              })
*/

function Card({
  element,
  Editor,
  pageType,
}: {
  element: any;
  Editor: React.ReactNode;
  pageType: string;
}) {
  const dispatch = useAppDispatch();
  const [title, setTitle] = useState(
    element.name === undefined ? element.code : element.name
  );

  const [elId, setElId] = useState("");
  const [type, setType] = useState<ElementType>();
  const params = useParams();

  useEffect(() => {
    setTitle(element.name === undefined ? element.code : element.name);
  }, [element.code, element.name]);
  return (
    <div className="m-2">
      <ElementCards
        editor={
          // @ts-ignore
          <Editor
            text={element}
            onUpdateStrings={(
              { target: { value } }: any,
              element: any,
              type: any,
              id: any
            ) => {
              setElId(id);
              setType(type);
              dispatch(
                changeElement({
                  content: { [element]: value },
                  type: type,
                  id: id,
                })
              );
            }}
            onUpdateRichText={(
              value: any,
              element: any,
              type: any,
              id: any
            ) => {
              setElId(id);
              setType(type);
              dispatch(
                changeElement({
                  content: { [element]: value },
                  type: type,
                  id: id,
                })
              );
            }}
          />
        }
        title={title}
        id={elId}
        onSave={() =>
          dispatch(
            changeUserElementToBackend({
              id: elId,
              type: type,
              elementData: element,

              orgId: params.orgId || "",
            })
          )
        }
        onDelete={() => {
          dispatch(
            askToDelete({
              element: "element",
              elementToDelete: element,
              type: pageType,
            })
          );
        }}
      />
    </div>
  );
}
